<template>
  <PmDialogPure
    v-bind="separateAttrs($attrs).attributes"
    :class="$attrs.class"
    :title="titleNormalized"
    :can-be-closed="!isLoading"
    :error-message="errorMessage"
    :error-details="errorDetails"
    :text="text"
    :buttons="buttons"
    @cancel="emit('cancel')"
    @confirm="emit('confirm')"
  >
    <slot />
  </PmDialogPure>
</template>

<script setup lang="ts">
import { computed } from 'vue'

import PmDialogPure, {
  type Props as PropsDialogPanelPure,
} from '@/components/basics/PmDialog/PmDialogPure.vue'
import { separateAttrs } from '@/utilities/misc'
import { lowerFirst } from 'lodash-es'
import type { Get } from 'type-fest'
import { isEmptyString } from '@/utilities/string'

export interface Props {
  entity?: string
  subject?: string
  actionLabel: string
  isLoading?: boolean
  disabled?: boolean
  text?: string
  /** For overriding the automatically derived title */
  title?: string
  errorMessage?: PropsDialogPanelPure['errorMessage']
  errorDetails?: PropsDialogPanelPure['errorDetails']
  /** When the confirm button should submit a form */
  formId?: string
}

const props = withDefaults(defineProps<Props>(), {})

const emit = defineEmits<{
  confirm: []
  cancel: []
}>()

const titleNormalized = computed(() => {
  if (props.title && !isEmptyString(props.title)) return props.title
  return `${props.subject} ${lowerFirst(props.actionLabel)}?`
})

const buttons = computed(() => {
  if (props.errorMessage) return buttonsError.value
  return buttonsDefault.value
})

const buttonsDefault = computed<PropsDialogPanelPure['buttons']>(() => {
  let buttonConfirm: Get<PropsDialogPanelPure, 'buttons[0]'> = {
    label: props.actionLabel,
    action: 'confirm',
    variant: 'primary',
    icon: 'check',
    disabled: props.disabled,
    loading: props.isLoading,
  }

  if (props.formId) {
    buttonConfirm = {
      ...buttonConfirm,
      type: 'submit',
      form: props.formId,
    }
  }

  return [
    {
      label: 'Abbrechen',
      action: 'cancel',
      icon: 'close',
      disabled: props.isLoading || props.disabled,
    },
    buttonConfirm,
  ]
})

const buttonsError = computed<PropsDialogPanelPure['buttons']>(() => [
  {
    label: 'Schließen',
    action: 'cancel',
    icon: 'close',
  },
])
</script>
